import React from 'react';
import { Eye } from 'lucide-react';
import {
  STATUS_LABEL,
  formatDate,
  getDoctorVisitText,
  getFollowUp,
  getMedicinesText,
  getStatus,
  getSymptomsText,
  getTestsText,
} from '../../../utils/medicalHistory';

const FOLLOW_UP_TONE = {
  done: 'bg-green-100 text-green-800',
  upcoming: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-700',
  active: 'bg-amber-100 text-amber-800',
  none: '',
};
const STATUS_TONE = {
  open: 'bg-red-100 text-red-700',
  under_treatment: 'bg-yellow-100 text-yellow-800',
  closed: 'bg-green-200 text-green-900',
};

const HEADERS = ['Symptoms', 'Medicines', 'Tests', 'Dr. Visit', 'Follow Up', 'Status', 'View'];
const CELL = 'px-4 py-3 align-middle';
const SORT_ARROW = { asc: '↑', desc: '↓' };

/**
 * One page of check-ins as a table. Date is the only sortable column; the eye
 * opens that check-in's details. Closed check-ins get a green row.
 */
export default function MedicalHistoryTable({ rows, sortDirection, onToggleSort, onView }) {
  const now = new Date();

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[860px] text-left text-sm">
        <thead>
          <tr className="bg-sky-50 text-[15px] font-extrabold text-slate-800">
            <th scope="col" aria-sort={sortDirection === 'asc' ? 'ascending' : 'descending'} className={CELL}>
              <button
                type="button"
                onClick={onToggleSort}
                aria-label={`Date, sorted ${sortDirection === 'asc' ? 'oldest' : 'newest'} first. Click to reverse.`}
                className="flex items-center gap-1.5 font-extrabold"
              >
                Date
                <span aria-hidden="true" className="text-xs text-slate-500">
                  {SORT_ARROW[sortDirection]}
                </span>
              </button>
            </th>
            {HEADERS.map((header) => (
              <th key={header} scope="col" className={`${CELL} ${header === 'View' ? 'text-center' : ''}`}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="font-semibold text-slate-700">
          {rows.map((record) => {
            const status = getStatus(record);
            const followUp = getFollowUp(record, now);
            const date = formatDate(record.date);
            return (
              <tr
                key={record._id}
                className={`border-t border-gray-100 ${status === 'closed' ? 'bg-green-50' : 'bg-white'}`}
              >
                <td className={`${CELL} whitespace-nowrap`}>{date}</td>
                <td className={CELL}>{getSymptomsText(record)}</td>
                <td className={CELL}>{getMedicinesText(record)}</td>
                <td className={CELL}>{getTestsText(record)}</td>
                <td className={`${CELL} whitespace-nowrap`}>{getDoctorVisitText(record)}</td>
                <td className={CELL}>
                  {followUp.tone === 'none' ? (
                    followUp.text
                  ) : (
                    <span className={`inline-block rounded-lg px-3 py-1.5 text-center ${FOLLOW_UP_TONE[followUp.tone]}`}>
                      {followUp.text}
                    </span>
                  )}
                </td>
                <td className={CELL}>
                  <span className={`inline-block min-w-[6.5rem] rounded-lg px-3 py-1.5 text-center ${STATUS_TONE[status]}`}>
                    {STATUS_LABEL[status]}
                  </span>
                </td>
                <td className={`${CELL} text-center`}>
                  <button
                    type="button"
                    onClick={(event) => onView(record, event.currentTarget)}
                    aria-label={`View check-up on ${date}`}
                    className="rounded-full p-2 text-blue-600 transition-colors hover:bg-blue-50"
                  >
                    <Eye size={22} aria-hidden="true" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
